import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import {
  BehaviorSubject,
  catchError,
  firstValueFrom,
  map,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { Login, LoginResponse } from '../../models/interfaces/auth.interface';
import { handleError } from '../tools/handle-error';
import { TOKEN } from '../keys/keys';
import { Usuario } from '../../models/interfaces/usuario';
import { SistemaContextService } from './sistema-context.service';
import { SistemaService } from './sistema.service';
import { PermisosStateService } from './permisos-state.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.url}/v1/auth/`;
  private readonly injector = inject(Injector);
  private sistemaService?: SistemaService;

  login(credenciales: Login, param?: Params) {
    const dir = `${this.url}login`;
    return this.http
      .post<LoginResponse | { url: string }>(dir, credenciales)
      .pipe(
        catchError((err) => handleError(err)),
        switchMap(async (data) => {
          if ('url' in data) {
            window.location.href = data.url;
          } else {
            this.saveToken(data.token);
            this.pushNewUser(data.user);
            await this.redirect(param);
          }
          return data;
        })
      );
  }

  //* Inyección de el servicio de Cookies --------------------->
  private readonly cookie = inject(CookieService);
  /** @description Guarda el token en las cookies */
  private saveToken(token: string) {
    this.cookie.set(TOKEN, token, undefined, '/');
  }
  /** @description Remueve el token de las cookies */
  private removeToken() {
    this.cookie.set(TOKEN, '', undefined, '/');
  }

  //* Inyección del servicio de Router para navegar ------------>
  private readonly router = inject(Router);
  private readonly sistemaContext = inject(SistemaContextService);
  private readonly permisosState = inject(PermisosStateService);

  logout() {
    this.removeToken();
    this.sistemaContext.limpiarSistema(); // Limpiar el sistema seleccionado
    this.router.navigate(['login'], { replaceUrl: true });
  }
  /**
   * @description
   * Una vez que pasa las autenticaciones, redirigimos al usuario.
   * @param param Un param en la url para redirigir a una sección específica
   * */
  private async redirect(param: any) {
    if (param ? param['redirect'] : false) {
      const ruta = param['redirect'];
      const p = { ...param };
      delete p['redirect'];
      await this.router.navigate([ruta], { queryParams: p });
    } else {
      if (!this.sistemaService) {
        this.sistemaService = this.injector.get(SistemaService);
      }

      try {
        const sistemas = await firstValueFrom(this.sistemaService.obtenerMisSistemas());

        if (sistemas.length === 1) {
          // Si tiene un solo sistema, cargar permisos antes de establecer el contexto
          const codigo = sistemas[0].codigo;
          const empresaId = undefined; // empresa se calculará más adelante si aplica
          try {
            await firstValueFrom(this.permisosState.cargarPermisos(codigo, empresaId));
          } catch (err) {
            console.error('No se pudieron cargar permisos al iniciar sesión:', err);
          }

          // Ahora establecer contexto y redirigir
          this.sistemaContext.establecerSistema(codigo);
          const ruta = codigo === 'TELEMETRIA' ? '/dashboard' : '/recorredores';
          await this.router.navigate([ruta]);
          return;
        }

        //! Si tiene múltiples sistemas o ninguno, ir al selector
        await this.router.navigate(['/selector-sistema']);
      } catch {
        //! En caso de error, redirigir al selector por defecto
        await this.router.navigate(['/selector-sistema']);
      }
    }
  }

  //* Guardamos el estado del Usuario -------------------------->
  private usuario = new BehaviorSubject<Usuario | null>(null);
  public readonly user$ = this.usuario.asObservable();
  /** @description Guardamos el usuario en una variable tipo Behavior Subject */
 private pushNewUser(usuario: Usuario) {
  this.usuario.next(usuario);
    this.permisosState.setUsuario(usuario as any);
}

  /**
   * @description Retorna la información del usuario
   * @returns Información del usuario
   */
  async returnUserInfo(): Promise<Usuario | null> {
    const direction = `${this.url}info`;
    try {
      //* Obtener la información del usuario, ya sea desde el observable local o desde la API
      const userData = await firstValueFrom(this.getUserData(direction));
      return userData;
    } catch (error) {
      //* Manejar errores y cerrar sesión
      this.handleLogout(error);
      return null;
    }
  }
  /**
   * @description
   * Intenta obtener la información del usuario desde el observable `usuario`.
   * Si `usuario` es `null`, realiza una solicitud HTTP para obtener los datos.
   */
  private getUserData(direction: string) {
    return this.usuario.pipe(
      take(1),
      switchMap((data) =>
        data != null ? of(data) : this.fetchAndCacheUser(direction)
      )
    );
  }
  /**
   * @description
   * Realiza una solicitud HTTP para obtener los datos del usuario y
   * actualiza el observable `usuario` con el nuevo valor.
   */
  private fetchAndCacheUser(direction: string) {
    return this.http.get<Usuario>(direction).pipe(
      map((data) => data),
      tap((userToken) => this.pushNewUser(userToken)),
      catchError((error) => {
        this.handleLogout(error);
        return throwError(
          () => new Error('No se pudo recuperar la información del usuario')
        );
      })
    );
  }
  /**
   * @description
   * Maneja el cierre de sesión y los errores arrojados durante la solicitud de datos.
   * Puede ampliarse para incluir más acciones de limpieza si es necesario.
   */
  private handleLogout(error?: any) {
    //! Log para más contexto del error
    console.error('Error al recuperar información de usuario:', error);

    this.logout();
  }

  //* Comprobamos el token ------------------------------------>
  checkToken(token: string) {
    const direction = `${this.url}check`;
    return this.http.post<boolean>(direction, { token }).pipe(
      catchError((e) => handleError(e)),
      map((data) => data)
    );
  }

  private readonly route = inject(ActivatedRoute);
  async navigateToHome() {
    //! Recordar que el parámetro para la re dirección es ?redirect=/home...
    const params = await firstValueFrom(this.route.queryParams);
    const token = this.cookie.get(TOKEN);
    if (token) {
      if (params['redirect']) {
        const ruta = params['redirect'];
        const queryParams = { ...params };
        delete queryParams['redirect'];
        this.router.navigate([ruta], { queryParams });
      } else {
        if (!this.sistemaService) {
          this.sistemaService = this.injector.get(SistemaService);
        }

        // Verificar cuántos sistemas tiene el usuario
          this.sistemaService.obtenerMisSistemas().subscribe({
            next: async (sistemas: any[]) => {
              if (sistemas.length === 1) {
                // Si tiene un solo sistema, cargar permisos antes de establecer contexto
                const codigo = sistemas[0].codigo;
                const empresaId = undefined;
                try {
                  await firstValueFrom(this.permisosState.cargarPermisos(codigo, empresaId));
                } catch (err) {
                  console.error('No se pudieron cargar permisos en navigateToHome:', err);
                }
                // Establecer sistema y redirigir
                this.sistemaContext.establecerSistema(codigo);
                const ruta = codigo === 'TELEMETRIA' ? '/dashboard' : '/recorredores';
                this.router.navigate([ruta]);
              } else {
                // Si tiene múltiples sistemas o ninguno, ir al selector
                this.router.navigate(['/selector-sistema']);
              }
            },
          error: () => {
            // En caso de error, redirigir al selector por defecto
            this.router.navigate(['/selector-sistema']);
          }
        });
      }
    }
  }

  register(data: Partial<Usuario>) {
    const url = this.url + 'register';
    return this.http.post<Usuario>(url, data).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        return data;
      })
    );
  }

  forgotPassword(username: string) {
    const url = this.url + 'forgot-password';
    return this.http.post<{ ok: boolean; message: string }>(url, { username }).pipe(
      catchError((err) => handleError(err)),
      map((data) => data)
    );
  }

  changePassword(clave: string) {
    const url = this.url + 'changePassword';
    return this.http.put<boolean>(url, { contrasenia: clave }).pipe(
      catchError((err) => handleError(err)),
      map((data) => data)
    );
  }
  getToken(): string | null {
    return this.cookie.get(TOKEN);
  }
}
